// Token + identity helpers. Single source for the localStorage key, the
// Bearer header, and the /api/auth/me probe - previously copy-pasted
// across RequireAuth, AuthGoogleReturn, api/notifications and api/mock.

import type { CurrentUser } from "@duralex/shared";

export const TOKEN_STORAGE_KEY = "token";

/** Mirrors MIN_PASSWORD_LENGTH in server services/clientAuth.ts - refuse
    locally what the server refuses. One number for both sign-in doors. */
export const MIN_PASSWORD_LENGTH = 10;

/** Shown wherever storeToken fails - one string, both sign-in doors. */
export const STORAGE_REFUSED_MESSAGE =
  "Your browser refused to keep the sign-in. Check private-browsing or storage settings and try again.";

export function getAuthHeaders(): Record<string, string> {
  if (typeof window === "undefined") return {};
  const token = window.localStorage.getItem(TOKEN_STORAGE_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function clearToken(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(TOKEN_STORAGE_KEY);
}

/** A return_to the router may follow: same-origin paths only. "//host"
    and absolute URLs collapse to "/" instead of stranding or leaking the
    visitor. */
export function safeReturnTo(raw: string | null): string {
  if (!raw) return "/";
  if (!raw.startsWith("/")) return "/";
  if (raw.startsWith("//")) return "/";
  return raw;
}

/** False when the browser refuses storage (incognito, quota) - the caller
    tells the user instead of navigating into a logged-out shell. */
export function storeToken(token: string): boolean {
  try {
    window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    return true;
  } catch {
    return false;
  }
}

/** The password was right but a code is still owed. The pending token
    lives in component state only - never in TOKEN_STORAGE_KEY, where
    RequireAuth would mistake it for a session. */
export type StaffMfaChallenge = { pendingToken: string; method: "totp" | "email" };

export type StaffAuthResult =
  | { ok: true; token: string }
  | { ok: true; mfa: StaffMfaChallenge }
  | { ok: false; status: number; message: string };

async function postForToken(path: string, payload: unknown): Promise<StaffAuthResult> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(payload),
  });
  const body = (await res.json().catch(() => ({}))) as {
    token?: string;
    mfa?: { pendingToken?: string; method?: string };
    error?: string;
  };
  if (res.ok && body.mfa?.pendingToken) {
    return {
      ok: true,
      mfa: {
        pendingToken: body.mfa.pendingToken,
        method: body.mfa.method === "email" ? "email" : "totp",
      },
    };
  }
  if (!res.ok || !body.token) {
    return {
      ok: false,
      status: res.status,
      message: body.error ?? "Sign-in failed. Please try again.",
    };
  }
  return { ok: true, token: body.token };
}

/** POST /api/auth/login. The server answers one generic 401 for every
    rejection on purpose; its message is shown as-is. The response's user
    object is ignored - RequireAuth probes /me for the canonical shape. */
export function staffLogin(email: string, password: string): Promise<StaffAuthResult> {
  return postForToken("/api/auth/login", { email, password });
}

/** POST /api/auth/set-password - spends the emailed link and signs in. */
export function setStaffPassword(token: string, password: string): Promise<StaffAuthResult> {
  return postForToken("/api/auth/set-password", { token, password });
}

/** POST /api/auth/login/otp - the second step. Success is the same full
    sign-in the password door answers. */
export function postMfaCode(pendingToken: string, code: string): Promise<StaffAuthResult> {
  return postForToken("/api/auth/login/otp", { pendingToken, code });
}

export type MfaEmailSendResult =
  | { ok: true }
  | { ok: false; reason: "throttled" | "mail_unavailable" | "restart" | "request_failed" };

/** POST /api/auth/login/email-code - asks the server to mail a code for a
    pending sign-in. Honest outcomes: the password step is already behind,
    so nothing here hides whether mail went out. */
export async function requestMfaEmailCode(pendingToken: string): Promise<MfaEmailSendResult> {
  const res = await fetch("/api/auth/login/email-code", {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ pendingToken }),
  });
  if (res.status === 401) return { ok: false, reason: "restart" };
  if (res.status === 429) return { ok: false, reason: "throttled" };
  if (!res.ok) return { ok: false, reason: "request_failed" };
  const body = (await res.json().catch(() => ({}))) as { ok?: boolean; reason?: string };
  if (body.ok) return { ok: true };
  return { ok: false, reason: body.reason === "throttled" ? "throttled" : "mail_unavailable" };
}

/** POST /api/auth/verify-email - spends the mailed confirm link. By design
    the server answers ok with no session: sign-in stays a separate step. */
export async function verifyStaffEmail(
  token: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  const res = await fetch("/api/auth/verify-email", {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ token }),
  });
  const body = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
  if (!res.ok || !body.ok) {
    return { ok: false, message: body.error ?? "Could not confirm the address. Please try again." };
  }
  return { ok: true };
}

export type StaffForgotResult =
  | { ok: true }
  | { ok: false; reason: "mail_unavailable" | "rate_limited" | "request_failed" };

/** POST /api/auth/forgot-password. ok:false is only ever about the firm's
    mail being down or unproven - never about the address. */
export async function requestStaffPasswordLink(email: string): Promise<StaffForgotResult> {
  const res = await fetch("/api/auth/forgot-password", {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ email }),
  });
  if (res.status === 429) return { ok: false, reason: "rate_limited" };
  if (!res.ok) return { ok: false, reason: "request_failed" };
  const body = (await res.json()) as { ok?: boolean };
  return body.ok ? { ok: true } : { ok: false, reason: "mail_unavailable" };
}

export type MfaCeremonyError = { ok: false; status: number; message: string };

const CEREMONY_FAILED = "Something went wrong. Please try again.";

/** The reply was not JSON at all, so nothing on the server spoke: the proxy
    or the server itself is down. Saying "something went wrong" there reads
    as a broken feature when the truth is that nobody answered. */
export const REQUEST_FAILED = "Could not reach the server. Please try again.";

function failureMessage(body: { error?: string } | null): string {
  return body?.error ?? (body ? CEREMONY_FAILED : REQUEST_FAILED);
}

function ceremonyFailure(res: Response, body: { error?: string } | null): MfaCeremonyError {
  return { ok: false, status: res.status, message: failureMessage(body) };
}

async function postAuthedJson(path: string, payload: unknown): Promise<Response> {
  return fetch(path, {
    method: "POST",
    headers: { "content-type": "application/json", ...getAuthHeaders() },
    credentials: "same-origin",
    body: JSON.stringify(payload),
  });
}

export type MfaSetupStart = { ok: true; qrDataUri: string; secret: string } | MfaCeremonyError;

/** POST /api/auth/mfa/setup - mints a pending secret for the signed-in
    user. Repeat calls supersede the pending secret; a connected factor
    answers 409 and the server's words are shown as-is. The response's
    otpauthUri is dropped: the QR already encodes it, and unused
    secret-bearing data has no place in client state. */
export async function startMfaSetup(): Promise<MfaSetupStart> {
  const res = await postAuthedJson("/api/auth/mfa/setup", {});
  const body = (await res.json().catch(() => null)) as {
    qrDataUri?: string;
    secret?: string;
    error?: string;
  } | null;
  if (!res.ok || !body?.qrDataUri || !body.secret) {
    return ceremonyFailure(res, body);
  }
  return { ok: true, qrDataUri: body.qrDataUri, secret: body.secret };
}

export type MfaConfirmResult = { ok: true; recoveryCodes: string[] } | MfaCeremonyError;

/** POST /api/auth/mfa/confirm - one true code turns the factor on and
    pays out the recovery codes, shown exactly once. */
export async function confirmMfaSetup(code: string): Promise<MfaConfirmResult> {
  const res = await postAuthedJson("/api/auth/mfa/confirm", { code });
  const body = (await res.json().catch(() => null)) as {
    ok?: boolean;
    recoveryCodes?: string[];
    error?: string;
  } | null;
  if (!res.ok || !body?.ok || !Array.isArray(body.recoveryCodes)) {
    return ceremonyFailure(res, body);
  }
  return { ok: true, recoveryCodes: body.recoveryCodes };
}

/** POST /api/auth/mfa/disable - possession of the factor (an app code or
    a recovery code), not the session, is what may remove it. */
export async function disableMfa(code: string): Promise<{ ok: true } | MfaCeremonyError> {
  const res = await postAuthedJson("/api/auth/mfa/disable", { code });
  const body = (await res.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
  if (!res.ok || !body?.ok) {
    return ceremonyFailure(res, body);
  }
  return { ok: true };
}

/** GET /api/auth/password-options - the two facts the profile needs before
    it draws a control: whether this account has a password to change, and
    whether the firm's mail can carry a link to set one. */
export interface PasswordOptions {
  hasPassword: boolean;
  canSetByMail: boolean;
}

export async function fetchPasswordOptions(): Promise<PasswordOptions> {
  const res = await fetch("/api/auth/password-options", {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`password-options: ${res.status}`);
  return (await res.json()) as PasswordOptions;
}

export type ChangePasswordResult = { ok: true } | { ok: false; message: string };

/** POST /api/auth/change-password. The server's own words carry the refusal
    (a wrong current password, a short new one); a reply that is not JSON
    means nobody answered. */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<ChangePasswordResult> {
  const res = await postAuthedJson("/api/auth/change-password", {
    currentPassword,
    newPassword,
  });
  const body = (await res.json().catch(() => null)) as { token?: string; error?: string } | null;
  if (!res.ok) return { ok: false, message: failureMessage(body) };
  // The change ends every session, this one included; the reply carries the
  // replacement, and losing it drops the user out of the page they are on.
  if (!body?.token || !storeToken(body.token)) {
    return {
      ok: false,
      message: "Password changed, but this browser would not keep the new session. Sign in again.",
    };
  }
  return { ok: true };
}

export type MeResult = { ok: true; user: CurrentUser } | { ok: false; status: number };

/** Probe /api/auth/me. Non-2xx becomes {ok:false,status}; network errors throw. */
export async function fetchMe(): Promise<MeResult> {
  const res = await fetch("/api/auth/me", {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (!res.ok) return { ok: false, status: res.status };
  return { ok: true, user: (await res.json()) as CurrentUser };
}

export type ConnectUrlResult = { ok: true; url: string } | { ok: false; status: number };

/**
 * Asks the server for the Google consent URL. The endpoint is admin-only,
 * so it answers with JSON instead of redirecting - a plain link cannot
 * carry the Authorization header. The caller navigates.
 */
export async function fetchGoogleConnectUrl(): Promise<ConnectUrlResult> {
  const res = await fetch("/api/auth/google", {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (!res.ok) return { ok: false, status: res.status };
  const body = (await res.json()) as { url?: string };
  if (!body.url) return { ok: false, status: res.status };
  return { ok: true, url: body.url };
}

/**
 * Admin-only: revoke the firm Google grant and drop the stored tokens.
 * `revoked` is false when Google could not confirm the revocation - the
 * grant may still show under the account at myaccount.google.com.
 */
export async function disconnectGoogle(): Promise<{ revoked: boolean }> {
  const res = await fetch("/api/auth/google", {
    method: "DELETE",
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (res.status === 403) throw new Error("Only an admin can disconnect the firm Google account");
  if (!res.ok) throw new Error(`Disconnect failed (HTTP ${res.status})`);
  const body = (await res.json()) as { revoked?: boolean };
  return { revoked: Boolean(body.revoked) };
}

/** "Robert Bond, Esq." -> "RB". Falls back to "?" when nothing usable. */
export function initialsOf(fullName: string): string {
  const letters = fullName
    .trim()
    .split(/\s+/)
    .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ""))
    .filter(Boolean)
    .map((w) => w[0].toUpperCase());
  if (letters.length === 0) return "?";
  return (letters[0] + (letters[1] ?? "")).slice(0, 2);
}

/** The server's own reason out of a 4xx body, or the fallback: a 5xx body
    never speaks for the request. */
export async function refusal(res: Response, fallback: string): Promise<Error> {
  if (res.status < 500) {
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) return new Error(body.error);
    } catch {
      /* no JSON body */
    }
  }
  return new Error(fallback);
}

/** A link cannot carry the token. */
export async function fetchStaffBlob(url: string, kind = "PDF"): Promise<Blob> {
  const res = await fetch(url, {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (!res.ok) throw await refusal(res, `Could not download the ${kind} (${res.status}).`);
  return res.blob();
}

export async function downloadStaffFile(
  url: string,
  fileName: string,
  kind = "PDF",
): Promise<void> {
  const blobUrl = URL.createObjectURL(await fetchStaffBlob(url, kind));
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(blobUrl);
}
